import React from "react";
import { Link } from "react-router-dom";
import usePageTitle from "../../hooks/usePageTitle";

const FAQS = [
  {
    q: "How do fees work?",
    a: "Providers do not set custom platform take-rates outside CrunchAlpha rules. Performance fees (when applicable) use a high-water mark so the same equity peak is not charged twice. Exact rates and plan limits appear in Billing after you register — they can change, so the product is the source of truth, not a static marketing page.",
  },
  {
    q: "What is CrunchAlpha?",
    a: "CrunchAlpha is a non-custodial copy-trading platform. You keep funds in your own broker account. Investors can copy verified traders and analyst signal sets. Ranking uses AlphaRank™ — real performance and risk metrics, not marketing screenshots.",
  },
  {
    q: "Is this custodial? Where does my money sit?",
    a: "Non-custodial. Capital stays at your broker (MT4/MT5). CrunchAlpha syncs trades and signals via Expert Advisors (EA). We never hold your deposit.",
  },
  {
    q: "What are the three roles?",
    a: "Investor — copy traders and/or analysts, manage allocation and risk caps. Trader — connect a live MT account; followers can mirror your trades. Analyst — publish signal sets (entry/SL/TP) without a required live account; subscribers can auto-execute via EA.",
  },
  {
    q: "What is AlphaRank™ / AlphaScore?",
    a: "A 0–100 score from seven pillars (profitability, consistency, risk management, recovery, edge, discipline, track record). Grades run from A+ down to F. Risk flags (e.g. no stop-loss, high drawdown) are shown openly so you can judge quality beyond ROI.",
  },
  {
    q: "How does risk-normalized copy work?",
    a: "Lot size is scaled to your capital and allocation — not the provider’s raw lot. Example: provider trades 1.0 lot on $10,000; you allocate with $1,000 → system targets ~0.1 lot. You also set max lot, max daily loss %, and max open trades in Copy Engine settings.",
  },
  {
    q: "How do I start as an Investor?",
    a: "1) Register and verify email. 2) Add a broker account. 3) Download EA Investor, install on MT4/MT5, paste EA Key, enable AutoTrading. 4) Open Marketplace, pick a trader or signal set by AlphaRank. 5) Set allocation and subscribe.",
  },
  {
    q: "How do I start as a Trader?",
    a: "1) Register as Trader. 2) Add your MT account. 3) Download EA Publisher, attach to chart, paste EA Key, enable AutoTrading. 4) Closed trades build AlphaRank (about 20+ trades for a stable score). 5) Investors can follow you from Marketplace.",
  },
  {
    q: "How do I start as an Analyst?",
    a: "1) Register as Analyst. 2) Create a Signal Set and publish signals with entry, SL, and TP. 3) Virtual Score uses closed signals (same pillar logic as live traders). 4) Subscribers follow the set from Marketplace; execution can be auto via their EA.",
  },
  {
    q: "What is SignalSync™?",
    a: "Optional for traders: live trades can also be published as plan signals (thesis on record) so investors can follow a plan track beside the live track. Enable from the Trader dashboard when available on your account.",
  },
  {
    q: "Who sets pricing and fees?",
    a: "Platform rules apply to everyone — individual traders/analysts do not set custom take rates outside the model. Typical split is provider / platform (and affiliate when referred). Performance fees use high-water mark where applicable so you are not charged twice on the same peak equity.",
  },
  {
    q: "What does an affiliate earn?",
    a: "Affiliates can earn a share of the platform slice (tiered, capped — e.g. up to about 10% of that slice depending on program rules). Traders and analysts may still qualify for bonus pool rewards on top when criteria are met. See Affiliate / Share & Earn in your dashboard for your live terms.",
  },
  {
    q: "What is the bonus pool?",
    a: "An optional extra reward funded from part of the platform slice (illustratively ~5–10% of that slice) for providers who meet quality bars such as survivability, consistency, and follower retention. Not guaranteed; criteria can be updated by the platform.",
  },
  {
    q: "Manual vs Auto for analyst signals?",
    a: "Manual is for discretionary / preview workflows. Auto execution via EA is the main product path for subscribers. Performance fees tied to high-water mark apply on the auto/copy bucket as defined in billing.",
  },
  {
    q: "What subscription plans exist?",
    a: "Plans such as Basic and Premium control follower limits, how many providers you can follow, and advanced tools. Plan limits and performance fees are shown in your account Billing when you register. Performance fees on profit are separate from any subscription fee.",
  },
  {
    q: "Why do ghost or stuck positions appear?",
    a: "If a provider closes but your copy leg lags, the system reconciles automatically within a short window (often 1–2 minutes). If a position stays wrong beyond ~5 minutes, check EA connection and MT Journal, then contact support.",
  },
  {
    q: "Is this investment advice?",
    a: "No. CrunchAlpha provides tooling, transparent metrics, and execution rails. Past performance does not guarantee future results. Trading involves risk of loss. Read the Risk Disclosure before copying anyone.",
  },
];

export default function QAPage() {
  usePageTitle("Q&A");
  return (
    <div className="page-container-narrow" style={{ padding: "24px 0 40px", display: "flex", flexDirection: "column", gap: 14, background: "transparent" }}>
      <div>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: "var(--text-main)", margin: 0 }}>Q&A</h1>
        <p style={{ color: "var(--text-muted)", fontSize: 13, margin: "4px 0 0", lineHeight: 1.5 }}>
          Practical answers for new users — roles, AlphaRank, copy risk controls, fees, and safety. English default.
        </p>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {FAQS.map((item, i) => (
          <div
            key={i}
            style={{
              background: "var(--bg-surface)",
              border: "1px solid var(--border)",
              borderRadius: "var(--radius-lg)",
              padding: "14px 16px",
            }}
          >
            <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-main)", marginBottom: 6 }}>
              {i + 1}. {item.q}
            </div>
            <div style={{ fontSize: 13, color: "var(--text-muted)", lineHeight: 1.55 }}>{item.a}</div>
          </div>
        ))}
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, paddingTop: 4 }}>
        <Link
          to="/legal/risk"
          style={{
            padding: "8px 14px",
            borderRadius: "var(--radius-md)",
            border: "1px solid var(--border)",
            color: "var(--text-main)",
            fontSize: 13,
            fontWeight: 500,
            textDecoration: "none",
            background: "var(--bg-elevated)",
          }}
        >
          Risk Disclosure
        </Link>
        <Link
          to="/"
          style={{
            padding: "8px 14px",
            borderRadius: "var(--radius-md)",
            border: "1px solid var(--border)",
            color: "var(--text-muted)",
            fontSize: 13,
            fontWeight: 500,
            textDecoration: "none",
            background: "transparent",
          }}
        >
          Back to Home
        </Link>
      </div>
    </div>
  );
}
